import get from "lodash.get"
import { YYYYMMDD } from "./dateUtils.mjs"
import { flattenObject } from "./objectUtils.mjs"

/**
 * Replaces path parameters in a URL string with corresponding values from a params object.
 *
 * - Supports parameters in the form `{key}` or `:key` within the URL.
 * - Encodes parameter values using `encodeURIComponent`.
 * - Handles query strings by preserving them after replacements.
 * - Flattens nested objects in `params` before replacement.
 * - If the URL contains no placeholders, returns it unchanged.
 *
 * @param {string} url - The URL string containing path parameters.
 * @param {Object} [params={}] - An object containing parameter values to replace in the URL.
 * @param {object} [flattenOptions] - Options passed to `flattenObject` when flattening nested `params` (e.g. `skipNodesWithKey`, `skipCircularReferences`).
 * @returns {string} The URL with parameters replaced by their encoded values.
 *
 * @example
 * replaceUrlPathParams("/api/user/{id}/details", { id: 123 })
 * // "/api/user/123/details"
 *
 * @example
 * replaceUrlPathParams("/search/:query/results", { query: "test search" })
 * // "/search/test%20search/results"
 *
 * @example
 * replaceUrlPathParams("/resource/{type}/:id?active=true", { type: "user", id: 42 })
 * // "/resource/user/42?active=true"
 */
function replaceUrlPathParams(url, params = {}, flattenOptions) {
  if (!url.includes("{") && !url.includes(":")) return url

  let [newUrl, query] = url.split("?")

  const flatParams = flattenObject(params, flattenOptions)
  for (const key in flatParams) {
    let value
    try {
      value = encodeURIComponent(flatParams[key])
    } catch {
      continue
    } // Some types cannot be stringified, like Symbol.
    newUrl = newUrl.replaceAll(`:${key}`, value).replaceAll(`{${key}}`, value)
  }

  if (query) return `${newUrl}?${query}`
  return newUrl
}

/**
 * Replaces or removes URL query parameters based on a params object.
 *
 * - Parses the query string of the URL.
 * - For query values containing placeholders `{key}` or `:key`, replaces them with the corresponding value from `params`.
 * - Removes any query parameter whose resolved value is falsy or missing.
 * - Returns the URL with the updated query string or without it if no parameters remain.
 * - If the URL contains no placeholders (`{}` or `:`), returns it unchanged.
 *
 * @param {string} url - The URL containing query parameters to replace or remove.
 * @param {Object} [params={}] - The object with values for query parameter replacement.
 * @returns {string} The URL with query parameters replaced or removed as needed.
 *
 * @example
 * replaceOrRemoveUrlQueryParams("https://example.com/api?user={userId}&active=true", { userId: 123 })
 * // "https://example.com/api?user=123&active=true"
 *
 * @example
 * replaceOrRemoveUrlQueryParams("https://example.com/api?user={userId}&active=true", {})
 * // "https://example.com/api?active=true"
 *
 * @example
 * replaceOrRemoveUrlQueryParams("https://example.com/api?flag=:flag", { flag: false })
 * // "https://example.com/api"
 */
function replaceOrRemoveUrlQueryParams(url, params = {}) {
  if (!url.includes("{") && !url.includes(":")) return url

  const [newUrl, queryString] = url.split("?")
  if (!queryString) return newUrl

  const newQueryString = []
  for (const queryParam of queryString.split("&")) {
    let [key, value] = queryParam.split("=")
    if (!value) continue

    if (value?.includes("{")) {
      value = value.replace("{", "").replace("}", "")
      value = get(params, value)
    }

    if (value?.includes(":")) {
      value = value.replace(":", "")
      value = get(params, value)
    }

    if (value) newQueryString.push(`${key}=${value}`)
  }

  if (newQueryString.length) return `${newUrl}?${newQueryString.join("&")}`
  return newUrl
}

/**
 * Replaces both path and query parameters in a URL string using provided params.
 *
 * - First replaces path parameters (`{key}` and `:key`) via `replaceUrlPathParams`.
 * - Then replaces or removes query parameters containing placeholders via `replaceOrRemoveUrlQueryParams`.
 *
 * @param {string} url - The URL string with path and query parameters to replace.
 * @param {Object} params - The parameters object to use for replacements.
 * @param {object} [flattenOptions] - Options passed to `flattenObject` for nested `params` in path replacement.
 * @returns {string} The URL with all placeholders replaced or removed accordingly.
 *
 * @example
 * replaceUrlParams("/api/{id}?active={active}", { id: 123, active: true })
 * // "/api/123?active=true"
 *
 * @example
 * replaceUrlParams("/api/:id?active=:active", { id: 123 })
 * // "/api/123"
 */
function replaceUrlParams(url, params, flattenOptions) {
  return replaceOrRemoveUrlQueryParams(replaceUrlPathParams(url, params, flattenOptions), params)
}

/**
 * Generates a RFC 5987 compliant filename string for Content-Disposition headers.
 *
 * - Encodes the filename using UTF-8 and percent-encoding.
 * - Removes potentially problematic characters: carriage returns, newlines, quotes, semicolons, and backslashes.
 * - Formats the string as `filename*=UTF-8''encodedFilename`.
 *
 * @param {string} fileName - The original filename to encode.
 * @returns {string} The RFC 5987 compliant filename string.
 *
 * @example
 * getRfc5987fileName("example file.txt")
 * // "filename*=UTF-8''example%20file.txt"
 */
function getRfc5987fileName(fileName) {
  return `filename*=UTF-8''${encodeURIComponent(fileName.replace(/[\r\n"';\\]/g, ""))}`
}

/**
 * Extracts the filename from a Content-Disposition header string.
 *
 * - Supports both `filename=` and `filename*=UTF-8''` formats as per RFC 5987.
 * - Decodes percent-encoded filenames when using UTF-8 encoding.
 * - Returns `undefined` if no filename is found in the header.
 *
 * @param {string} contentDispositionHeader - The Content-Disposition header string.
 * @returns {string|undefined} The extracted filename, or `undefined` if not found.
 *
 * @example
 * getContentDispositionFileName('attachment; filename=example.txt')
 * // "example.txt"
 *
 * @example
 * getContentDispositionFileName("attachment; filename*=UTF-8''%E2%9C%94.txt")
 * // "✓.txt"
 */
function getContentDispositionFileName(contentDispositionHeader) {
  let filename
  if (contentDispositionHeader) {
    // Try to find out the filename from the content disposition `filename=*` rfc5987 value
    const rfc5987matches = /filename[^;=\n]*\*=((['"]).*?\2|[^;\n]*)/.exec(contentDispositionHeader)
    if (rfc5987matches != null && rfc5987matches[1]) {
      let [encoding /*lang*/, , ...encodedFilename] = rfc5987matches[1].split("'")
      encodedFilename = encodedFilename.join("'")
      if (encoding.toLowerCase() === "utf-8") {
        filename = decodeURIComponent(encodedFilename)
      } else {
        filename = encodedFilename
        // other encodings could be supported in the future here if needed.
      }
    } else {
      // Try to find out the filename from the content disposition `filename=` value
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDispositionHeader)
      if (matches != null && matches[1]) {
        filename = matches[1]
        // Remove surrounding quotes if present
        if (filename.startsWith('"') && filename.endsWith('"')) {
          filename = filename.slice(1, -1)
        }
      } else {
        const matches = /"([^"]*)"/.exec(contentDispositionHeader)
        if (matches != null && matches[1]) {
          filename = matches[1]
        }
      }
    }
  }
  return filename
}

/**
 * Parses the query parameters from a URL string and returns them as an object.
 *
 * - Extracts the query string portion after `?` in the URL.
 * - Converts query parameters into key-value pairs.
 * - Decodes URI components for keys and values.
 * - If no query parameters exist, returns an empty object.
 *
 * @param {string} url - The URL string that may contain query parameters.
 * @returns {Object} An object representing the query parameters as key-value pairs.
 *
 * @example
 * getUrlQueryParams("https://example.com/page?foo=bar&baz=qux")
 * // { foo: "bar", baz: "qux" }
 *
 * @example
 * getUrlQueryParams("https://example.com/page")
 * // {}
 */
function getUrlQueryParams(url) {
  const params = {}
  if (!url) return params

  const queryString = url.split("?")[1]
  if (!queryString) return params

  for (let param of queryString.split("&")) {
    let [key, value] = param.split("=")

    key = decodeURIComponent(key)
    value = value.length > 1 ? decodeURIComponent(value) : ""

    if (value === "true") value = true
    else if (value === "false") value = false

    params[key] = value
  }

  return params
}

/**
 * Converts an object of parameters into a URL query string.
 *
 * - Ignores parameters that are `undefined`, `null`, empty strings, or empty arrays.
 * - Encodes string values using `encodeURIComponent`.
 * - Joins array values into a comma-separated string after encoding each element.
 * - Formats Date objects as `YYYYMMDD` using a helper function `YYYYMMDD`.
 * - Returns an empty string if no valid parameters exist.
 *
 * @param {Object} params - The parameters object to convert.
 * @returns {string} The URL query string starting with `?`, or an empty string if no parameters.
 *
 * @example
 * toQueryString({ foo: "bar", baz: 42 })
 * // "?foo=bar&baz=42"
 *
 * @example
 * toQueryString({ tags: ["a", "b c"] })
 * // "?tags=a,b%20c"
 *
 * @example
 * toQueryString({ date: new Date(2023, 0, 2) }) // assuming YYYYMMDD formats as "20230102"
 * // "?date=20230102"
 *
 * @example
 * toQueryString({})
 * // ""
 */
function toQueryString(params) {
  const fields = Object.keys(params || {})
  if (!fields.length) return ""

  const queryString = fields
    .map(field => {
      let value = params[field]

      if (value === undefined || value === null) return
      if (typeof value === "string" && !value.trim()) return
      if (Array.isArray(value) && value.length === 0) return

      if (Array.isArray(params[field])) value = value.map(v => encodeURIComponent(v)).join(",")
      else if (value instanceof Date) value = YYYYMMDD(value)
      else if (typeof value === "string") value = encodeURIComponent(value.trim())

      return `${field}=${value}`
    })
    .filter(param => param)
    .join("&")

  return queryString ? `?${queryString}` : queryString
}

export { getRfc5987fileName, getContentDispositionFileName, getUrlQueryParams, replaceUrlParams, toQueryString }
